import React from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { isSSR } from '@front10/utils';
import withCommonEMContainer from '../_commons/CommonEMContainer';

//-------------------------------------
// Basic components here
//-------------------------------------
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Box from '../../elements/Box';
import Disclaimer from '../../others/Disclaimer';
import Skeleton from '../../others/Skeleton/SkeletonFaresGridBackgroundImage';
import Loading from '../../others/Loading';
import NoFeaturedPopularFaresAlert from '../../alerts/NoFeaturedPopularFaresAlert';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';
import FareListTwoColumns from '../../lists/FareListTwoColumns';
import FareItemCard from '../../items/FareItemCard';
import LoadMorePaginator from '../../pagination/LoadMorePaginator';
import NoFaresBehavior from '../../others/NoFaresBehavior/NoFaresBehavior';

//-------------------------------------
// Every mundo integration
//-------------------------------------
import { getFares } from '../../../_integrations/everymundo/fares';
import { getAirports } from '../../../_integrations/everymundo/airports';

import prePopulationDefaultProps from '../_commons/prepopulationDefaultProps/groupedRoutesAggregation';

import { useFilters, OriginDestinationLogic } from '../_commons/filters';
import { useFareItemProps } from '../_commons/tracking';
import { useListItemFocus, useListFocusHandlers } from '../_commons/list';
import InlineFilters from '../../controls/InlineFilters/InlineFilters';
import SecondaryFilters from '../../controls/SecondaryFilters';
import HeaderContainer from '../../elements/HeaderContainer';

function FaresGridBackgroundImage(props) {
  const {
    prePopulation,
    integrationSettings,
    paginationSettings,
    meta,
    pageContext,
    getAirports: getAirportsFn,
    getFares: getFaresFn,
    direction,
    faresTrasformationLogic,
    showFilters,
    initialDestinationCards,
    initialDepartureAirports,
    fareItemOnClickAction,
    filters: { availableFilters, ...filters },
    listSettings: fareList,
    showSkeletonScreen,
    loading,
    showLoading,
    noResultsPlaceHolder,
    alert,
    showDisclaimer,
    mainContentSettings,
    disclaimerContentSettings,
    disclaimer: disclaimerSettings,
    skeletonSettings,
    noFaresBehavior,
    theme,
    pagination,
    loadMoreContentSettings,
    // deprecated props
    filterContentSettings, // use filters.containerSettings
    locationSelector, // use filters.originDestinationSettings
    budgetSetting, // use filters.budgetSettings
    datesRangeSelector, // use filters.dateRangeSettings
    clearAll, // use filters.clearAllSettings
    originDestinationLogic,
    secondaryFilters,
    showSecondaryFilters,
    enableSEO = true
  } = props;

  const [className, style] = useStyles(props, { direction, theme });
  const myStyle = Object.assign({ minHeight: '600px' }, style);

  // Define filter props using deprecated container props (for backwards compatibility)
  // Don't rely on this props for new configurations, they will be removed in a future version
  const filtersConfig = {
    containerSettings: filterContentSettings,
    originDestinationSettings: {
      components: {
        origin: locationSelector,
        destination: locationSelector
      }
    },
    budgetSettings: budgetSetting,
    dateRangeSettings: datesRangeSelector,
    clearAllSettings: clearAll,
    ...filters
  };

  const { focusIndex, setFocusIndex, ...focusHandlers } = useListFocusHandlers();
  const secondaryFiltersRef = React.useRef(null);

  const {
    fares: { match, ...fares },
    tracking,
    dispatch,
    retry,
    ...filtersState
  } = useFilters({
    originDestinationLogic,
    filter: { prePopulation, integrationSettings, meta, pageContext, getAirports: getAirportsFn },
    initialData: initialDestinationCards,
    minInitialResults: noFaresBehavior === 'hidden' ? 1 : null,
    initialOrigins: initialDepartureAirports,
    getFares: getFaresFn,
    paginationSettings,
    faresTransformationLogic: faresTrasformationLogic,
    availableFilters: showFilters ? availableFilters : [],
    showSecondaryFilters,
    availableSecondaryFilter: secondaryFilters ? secondaryFilters.availableFilters : [],
    hideBudgetOnMultiCurrency: filters.hideBudgetOnMultiCurrency,
    showOrigin:
      filtersConfig.originDestinationSettings && filtersConfig.originDestinationSettings.showOrigin,
    showDestination:
      filtersConfig.originDestinationSettings &&
      filtersConfig.originDestinationSettings.showDestination,
    ...focusHandlers
  });

  let itemSettings = useFareItemProps(fareList.itemSettings, dispatch, fareItemOnClickAction);

  let newFareListSettings = { ...fareList };
  if (fares.data && fares.data.length === 1) {
    newFareListSettings = {
      ...newFareListSettings,
      columnNumbers: 1
    };
    itemSettings = {
      ...itemSettings,
      fareItemBackgroundImage: {
        ...itemSettings.fareItemBackgroundImage,
        imageResizingParams: {
          worker: 'https://assets.airtrfx.com/cdn-cgi/image/',
          height: 720,
          width: 1280,
          quality: 90,
          fit: 'crop',
          format: 'auto',
          opt: true
        },
        ...itemSettings.fareItemBackgroundImageOneItem
      }
    };
  }

  const [nextFocusIndex, listItemRef] = useListItemFocus({
    data: fares.data,
    focusIndex,
    setFocusIndex
  });

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }

  if (match('HIDDEN')) {
    return <NoFaresBehavior />;
  }
  function handleSeeMoreDeals() {
    tracking({ type: 'MORE_DEALS' });
    return true;
  }
  return (
    <Box
      position="relative"
      className={`${className} ${FaresGridBackgroundImage.componentName}`}
      style={myStyle}
      dataTestId="faresGridBackgroundImage"
    >
      {showLoading && match('LOADING') && <Loading {...loading} />}
      {showSkeletonScreen && match('SKELETON') && (
        <Skeleton {...skeletonSettings} position="absolute" width="100" />
      )}
      <HeaderContainer
        defaultTitleValue={'Trending destination'}
        handleSeeMoreDeals={handleSeeMoreDeals}
        {...props}
      />
      {showFilters && <InlineFilters {...filtersConfig} {...filtersState} />}
      {showSecondaryFilters && (
        <SecondaryFilters
          ref={secondaryFiltersRef}
          {...filtersConfig}
          {...secondaryFilters}
          {...filtersState}
          availableFilters={filtersState.availableSecondaryFilters}
          filter={{
            ...{ prePopulation, integrationSettings, meta, getAirports }
          }}
          loading={match('SKELETON')}
        />
      )}
      <Row {...mainContentSettings}>
        {match('ERROR') && (
          <Col>
            <ContainerErrorAlert {...alert} tryAgain={retry} />
          </Col>
        )}
        {match('NO_RESULTS') && (
          <Col>
            <NoFeaturedPopularFaresAlert {...noResultsPlaceHolder} />
          </Col>
        )}
        {match('COMPLETE') && (
          <Col>
            <FareListTwoColumns
              {...newFareListSettings}
              itemSettings={itemSettings}
              ItemComponentInstance={FareItemCard}
              itemsData={fares.data}
              focusOn={nextFocusIndex}
              focusReferences={(ref) => {
                listItemRef.current = ref;
              }}
            />
          </Col>
        )}
      </Row>
      {match('COMPLETE') && fares.hasMore() && (
        <Row {...loadMoreContentSettings}>
          <Col>
            <Box textAlign="center">
              <LoadMorePaginator
                isLoading={match('LOADING')}
                onClickAction={fares.loadMore}
                {...pagination}
              />
            </Box>
          </Col>
        </Row>
      )}
      {showDisclaimer && (
        <Row {...disclaimerContentSettings}>
          <Col>
            <Disclaimer
              labelKey="cmp-fares-grid-background-image-container-disclaimer"
              {...disclaimerSettings}
            />
          </Col>
        </Row>
      )}
    </Box>
  );
}

FaresGridBackgroundImage.propTypes = {
  /**
   * Props of alerts
   */
  alert: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to budget
   */
  budgetSetting: PropTypes.objectOf(PropTypes.any),
  /**
   * To be used to set if the container is a controlled one
   */
  containerType: PropTypes.string,
  /**
   * Props of clearAll
   */
  clearAll: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to dates range selector
   */
  datesRangeSelector: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to destination item
   */
  destinationItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Direction of the component
   */
  direction: PropTypes.oneOf(['rtl', 'ltr']),
  /**
   * Disclaimer Component
   */
  disclaimer: PropTypes.objectOf(PropTypes.any),
  /**
   * disclaimerContentSettings props
   */
  disclaimerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Action to be executed when clicking in fareItem
   */
  fareItemOnClickAction: PropTypes.func,
  /**
   * Possible ways to get fares from API
   */
  faresTrasformationLogic: PropTypes.oneOf(['grouped-routes', 'agreggation']),
  /**
   * Filters settings
   */
  filters: PropTypes.objectOf(PropTypes.any),
  /**
   * filterContentSettings props
   */
  filterContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Function for request to obtain airports
   */
  getAirports: PropTypes.func,
  /**
   * Function to getFares
   */
  getFares: PropTypes.func,
  /**
   * Used to prepopulate the component
   */
  initialDepartureAirports: PropTypes.arrayOf(PropTypes.any),
  /**
   * Used to prepopulate the component
   */
  initialDestinationCards: PropTypes.arrayOf(PropTypes.any),
  /**
   * Loading Component
   */
  loading: PropTypes.objectOf(PropTypes.any),
  /**
   * Load more content settings props
   */
  loadMoreContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to location selector
   */
  locationSelector: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to list
   */
  listSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * mainContentSettings props
   */
  mainContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Component Behavior when there are not results available
   */
  noFaresBehavior: PropTypes.oneOf(['hidden', 'showNoResults']),
  /**
   * Props of noResultsPlaceHolder
   */
  noResultsPlaceHolder: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to other fixed title
   */
  otherPopularDestinationsLabel: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to other variable title
   */
  otherPopularDestinationsValue: PropTypes.objectOf(PropTypes.any),
  /**
   * pageContext settings
   */
  pageContext: PropTypes.objectOf(PropTypes.any),
  /**
   * Pagination Component
   */
  pagination: PropTypes.objectOf(PropTypes.any),
  /**
   * Settings of the Pagination
   */
  paginationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Component PrePopulation
   */
  prePopulation: PropTypes.objectOf(PropTypes.any),
  /**
   * Show Airport Iata Code in locations
   */
  showAirportIataCode: PropTypes.bool,
  /**
   * Determine if the component will show the disclaimer
   */
  showDisclaimer: PropTypes.bool,
  /**
   * Determine if the component will show the filters
   */
  showFilters: PropTypes.bool,
  /**
   * Determine if the component will show the skeleton screen while
   * loading (Default value will be false)
   */
  showSkeletonScreen: PropTypes.bool,
  /**
   * Shows Loading Component
   */
  showLoading: PropTypes.bool,
  /**
   * Shows Pagination Component
   */
  showPagination: PropTypes.bool,
  /**
   * Determine if the component will show the title
   */
  showTitle: PropTypes.bool,
  /**
   * SkeletonSettings Component
   */
  skeletonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  titleKey: PropTypes.string,
  /**
   * Title Component
   */
  titleSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to translate
   */
  translate: PropTypes.func,
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  fareItemInteractionType: PropTypes.oneOf(['popup', 'deeplink']),
  /**
   * LocalizationData via moment to be used as a date translator
   * https://github.com/moment/moment/blob/develop/src/locale/es.js
   */
  localizationData: PropTypes.objectOf(PropTypes.any),
  /**
   * Parameters tracking
   */
  tracking: PropTypes.objectOf(PropTypes.any),
  /**
   * Theme object on the component
   */
  theme: PropTypes.objectOf(PropTypes.any),
  /**
   * All integration configurations
   */
  integrationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * All integration configurations
   */
  meta: PropTypes.objectOf(PropTypes.any),
  /**
   * Login to load origin and destination airports
   * same: Origin and Destination are pre-populated or not
   *      a single airport service call is done to fill out the Origin
   *      and Destinations filters (for performance purposes).
   * different: Origin and Destination are pre-populated or not
   *      2 airports service calls are done to fill out the Origin
   *      and Destinations filters.
   */
  originDestinationLogic: PropTypes.oneOf([
    OriginDestinationLogic.SAME,
    OriginDestinationLogic.DIFFERENT
  ]),
  /**
   * Show the SubTitle Component
   */
  showSubTitle: PropTypes.bool,
  /**
   * subHeaderContentSettings props
   */
  subHeaderContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  subTitleKey: PropTypes.string,
  /**
   * SubTitle Component
   */
  subTitleSettings: PropTypes.objectOf(PropTypes.any)
};
FaresGridBackgroundImage.defaultProps = {
  fareItemInteractionType: 'popup',
  alert: {
    bgColor: 'light',
    textAlign: 'center',
    padding: '3',
    rounded: '@theme:card.rounded',
    labelKeyTitle: 'cmp-container-error-message',
    labelKeySubTitle: 'cmp-container-error-reload-deals'
  },
  budgetSetting: {},
  containerType: 'stateful',
  clearAll: {
    color: 'link',
    bgColor: 'transparent',
    textColor: 'light-18',
    borderNone: true,
    icon: {
      iconColor: 'light-18'
    }
  },
  datesRangeSelector: {},
  destinationItem: {},
  direction: 'ltr',
  disclaimer: {},
  disclaimerContentSettings: {
    marginTop: '2'
  },
  fareItemOnClickAction: null,
  faresTrasformationLogic: 'grouped-routes',
  filters: {
    availableFilters: ['OriginDestination', 'SingleDateRangePicker', 'Budget']
  },
  filterContentSettings: {
    marginWidth: '-5px'
  },
  getAirports,
  getFares,
  initialDepartureAirports: null,
  initialDestinationCards: null,
  loading: {},
  loadMoreContentSettings: {
    marginTop: '2'
  },
  locationSelector: {},
  listSettings: {},
  mainContentSettings: {
    marginTop: '3'
  },
  noFaresBehavior: 'hidden',
  noResultsPlaceHolder: {
    bgColor: 'light',
    rounded: '@theme:card.rounded'
  },
  otherPopularDestinationsLabel: {},
  otherPopularDestinationsValue: {},
  pageContext: {
    deviceCategory: 'DESKTOP',
    siteEdition: 'en_HK'
  },
  pagination: {},
  paginationSettings: {
    type: 'load_more',
    step: 6,
    limit: 30,
    initialVisibleItems: 10
  },
  prePopulation: prePopulationDefaultProps,
  showAirportIataCode: true,
  showDisclaimer: true,
  showFilters: true,
  showSkeletonScreen: true,
  showLoading: true,
  showPagination: true,
  showTitle: true,
  skeletonSettings: {
    style: { backgroundColor: 'white' }
  },
  titleKey: 'cmp-fares-grid-background-image-container-title',
  titleSettings: {
    margin: '0',
    textAlign: 'left'
  },
  translate: () => {},
  localizationData: null,
  tracking: {
    flexibleDates: false,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'faresGridBackgroundImage'
  },
  theme: null,
  integrationSettings: {},
  meta: null,
  originDestinationLogic: OriginDestinationLogic.SAME,
  showSubTitle: false,
  subHeaderContentSettings: {},
  subTitleKey: 'cmp-fare-grid-background-image-sub-title',
  subTitleSettings: {}
};
FaresGridBackgroundImage.componentName = 'FaresGridBackgroundImage';

// HOC with common functionality to all containers
export default withCommonEMContainer(FaresGridBackgroundImage);
